import {
  LargeHeadingStyled,
  SectionHeaderStyled,
} from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon, Select } from "@nubeteck/components";
import { ArrayUtils } from "@nubeteck/utils";
import { Checkbox, Input, Layout, Table, Skeleton } from "antd";
import { StrapiError } from "../../StrapiError";
import { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import {
  useErrorSavingDataToast,
  useSuccessSelectionUpdatedToast,
  useSuccessRegisterToast,
  toastErrorStyle,
} from "src/constants";
import {
  useCreateRequestAdminMutation,
  useGetDataSourcesQuery,
  useGetFieldTypesQuery,
  useGetRequestByIdAdminMutation,
  useGetRequestsAdminMutation,
  useGetRequestTypesQuery,
  useUpdateRequestAdminMutation,
  useUpdateRequestStateAdminMutation,
} from "src/services/requestsServices";
import { useGetServicesMutation } from "src/services/servicesServices";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaRequestConfig";
interface IOption {
  opcionId: number;
  opcion: string;
}

interface ICampo {
  campoId: number;
  esObligatorio: boolean;
  esAdministrativo: boolean;
  campoNombre: string;
  tipoCampoId: number;
  opciones?: IOption[] | string;
  fuenteDatos?: number;
  informacionAdicional: string;
}

interface ISolicitudes {
  solicitudId: number;
  estadoId: number;
  solicitudNombre: string;
  urlWebHook?: string;
  authWebhook?: string;
  requierePago: boolean;
  campos: ICampo[];
}

const RequestsConfig: React.FC = () => {
  const [form] = Form.useForm<ISolicitudes>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [requestId, setRequestId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [solicitudTipoId, setSolicitudTipoId] = React.useState();
  const [requierePagoValue, setRequierePagoValue] = React.useState(false);

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const [translatedData, setTranslatedData] = React.useState<{
    nombres: { [key: string]: string };
    estados: { [key: string]: string };
    tiposSolicitud: { [key: string]: string };
    servicios: { [key: string]: string };
    tiposCampos: { [key: string]: string };
    fuentesDatos: { [key: string]: string };
  }>({
    nombres: {},
    estados: {},
    tiposSolicitud: {},
    servicios: {},
    tiposCampos: {},
    fuentesDatos: {},
  });
  const showErrorSavingToast = useErrorSavingDataToast();
  const showSuccessSelectionUpdatedToast = useSuccessSelectionUpdatedToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const formularioCampos = Form.useWatch(["campos"], form);
  const formRef = React.useRef<FormInstance>(null);

  const { data: tiposCampos } = useGetFieldTypesQuery({ refetchOnMountOrArgChange: true });
  const { data: fuentesDatos } = useGetDataSourcesQuery({ refetchOnMountOrArgChange: true });
  const { data: solicitudesTipos } = useGetRequestTypesQuery({ refetchOnMountOrArgChange: true });
  const [getServices, { data: servicios }] = useGetServicesMutation();
  const [getRequests, { data, isLoading }] = useGetRequestsAdminMutation();
  const [
    createRequest,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, isError: isErrorCreate },
  ] = useCreateRequestAdminMutation();
  const [getRequestById, { data: requestById, isLoading: isLoadingById }] =
    useGetRequestByIdAdminMutation();
  const [
    updateRequest,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, isError: isErrorUpdate },
  ] = useUpdateRequestAdminMutation();
  const [
    updateRequestState,
    {
      isLoading: isLoadingState,
      isSuccess: isSuccessState,
      isError: isErrorState,
      error: errorState,
    },
  ] = useUpdateRequestStateAdminMutation();

  React.useEffect(() => {
    getRequests();
    getServices();
    setRequierePagoValue(false);
    setSolicitudTipoId(undefined);
  }, [
    getRequests,
    getServices,
    isSuccessCreate,
    isSuccessUpdate,
    isErrorCreate,
    isErrorUpdate,
    isSuccessState,
  ]);

  React.useEffect(() => {
    if (isErrorCreate) showErrorSavingToast();

    if (isSuccessCreate) {
      showSuccessToast();
      form.resetFields();
    }
    setRequestId(0);
    setDrawerOpen(false);
  }, [isSuccessCreate, form, isErrorCreate, showSuccessToast, showErrorSavingToast]);

  React.useEffect(() => {
    if (isErrorUpdate) showErrorSavingToast();

    if (isSuccessUpdate) {
      showSuccessToast();
      form.resetFields();
      setRequestId(0);
    }

    setDrawerOpen(false);
  }, [isSuccessUpdate, form, isErrorUpdate, showSuccessToast, showErrorSavingToast]);

  React.useEffect(() => {
    if (isErrorState) {
      // Keep using errorState?.data?.message for dynamic error messages from API
      toast.error(errorState?.data?.message, toastErrorStyle);
    }

    if (isSuccessState) {
      showSuccessSelectionUpdatedToast();
    }
  }, [errorState, isSuccessState, isErrorState, showSuccessSelectionUpdatedToast]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;
      const hasTiposSolicitud = solicitudesTipos?.data && Array.isArray(solicitudesTipos.data) && solicitudesTipos.data.length > 0;
      const hasServicios = servicios?.data && Array.isArray(servicios.data) && servicios.data.length > 0;
      const hasTiposCampos = tiposCampos?.data && Array.isArray(tiposCampos.data) && tiposCampos.data.length > 0;
      const hasFuentesDatos = fuentesDatos?.data && Array.isArray(fuentesDatos.data) && fuentesDatos.data.length > 0;

      if (!hasData && !hasTiposSolicitud && !hasServicios && !hasTiposCampos && !hasFuentesDatos) {
        setTranslatedData({
          nombres: {},
          estados: {},
          tiposSolicitud: {},
          servicios: {},
          tiposCampos: {},
          fuentesDatos: {},
        });
        return;
      }

      const nombresTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};
      const tiposSolicitudTranslations: { [key: string]: string | null } = {};
      const serviciosTranslations: { [key: string]: string | null } = {};
      const tiposCamposTranslations: { [key: string]: string | null } = {};
      const fuentesDatosTranslations: { [key: string]: string | null } = {};

      const translationPromises: Promise<void>[] = [];

      // Traducir datos de la tabla
      if (hasData) {
        data.data.forEach((record: ISolicitudes) => {
          if (record.solicitudNombre && !nombresTranslations.hasOwnProperty(record.solicitudNombre)) {
            nombresTranslations[record.solicitudNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, record.solicitudNombre, locale).then((translated) => {
                nombresTranslations[record.solicitudNombre] = translated;
              })
            );
          }
          const estadoNombre = (record as any).estadoNombre;
          if (estadoNombre && !estadosTranslations.hasOwnProperty(estadoNombre)) {
            estadosTranslations[estadoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, estadoNombre, locale).then((translated) => {
                estadosTranslations[estadoNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir tipos de solicitud
      if (hasTiposSolicitud) {
        solicitudesTipos.data.forEach((ts: any) => {
          if (ts.tipoNombre && !tiposSolicitudTranslations.hasOwnProperty(ts.tipoNombre)) {
            tiposSolicitudTranslations[ts.tipoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, ts.tipoNombre, locale).then((translated) => {
                tiposSolicitudTranslations[ts.tipoNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir servicios
      if (hasServicios) {
        servicios.data.forEach((sv: any) => {
          if (sv.servicioNombre && !serviciosTranslations.hasOwnProperty(sv.servicioNombre)) {
            serviciosTranslations[sv.servicioNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, sv.servicioNombre, locale).then((translated) => {
                serviciosTranslations[sv.servicioNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir tipos de campos
      if (hasTiposCampos) {
        tiposCampos.data.forEach((tc: any) => {
          if (tc.tipoCampoNombre && !tiposCamposTranslations.hasOwnProperty(tc.tipoCampoNombre)) {
            tiposCamposTranslations[tc.tipoCampoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, tc.tipoCampoNombre, locale).then((translated) => {
                tiposCamposTranslations[tc.tipoCampoNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir fuentes de datos
      if (hasFuentesDatos) {
        fuentesDatos.data.forEach((fd: any) => {
          if (fd.fuenteDatoNombre && !fuentesDatosTranslations.hasOwnProperty(fd.fuenteDatoNombre)) {
            fuentesDatosTranslations[fd.fuenteDatoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, fd.fuenteDatoNombre, locale).then((translated) => {
                fuentesDatosTranslations[fd.fuenteDatoNombre] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);
      setTranslatedData({
        nombres: nombresTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
        tiposSolicitud: tiposSolicitudTranslations as { [key: string]: string },
        servicios: serviciosTranslations as { [key: string]: string },
        tiposCampos: tiposCamposTranslations as { [key: string]: string },
        fuentesDatos: fuentesDatosTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data, solicitudesTipos?.data, servicios?.data, tiposCampos?.data, fuentesDatos?.data, locale]);

  const onClickEditRequest = React.useCallback(
    (record: ISolicitudes) => {
      setRequestId(record.solicitudId);
      getRequestById(record.solicitudId);
    },
    [getRequestById],
  );

  React.useEffect(() => {
    if (requestById?.data) {
      setSolicitudTipoId(requestById.data.solicitudTipoId);
      setRequierePagoValue(requestById.data.requierePago);
      form.setFieldsValue({
        ...requestById.data,
        campos: requestById.data?.campos?.map((ca: ICampo) => ({
          campoNombre: ca.campoNombre,
          esObligatorio: ca.esObligatorio,
          esAdministrativo: ca.esAdministrativo,
          informacionAdicional: ca.informacionAdicional,
          tipoCampoId: ca.tipoCampoId,
          campoId: ca.campoId,
          fuenteDatos: ca.fuenteDatos,
          opciones:
            ca.opciones && Object.keys(ca.opciones)?.length > 0
              ? ca.fuenteDatos == 1
                ? ca.opciones
                : (ca.opciones as IOption[])[0]?.opcion
              : undefined,
        })),
      });
      setDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestById]);

  const changeRequestState = React.useCallback(
    (id: number) => {
      updateRequestState(id);
    },
    [updateRequestState],
  );

  const submit = (values: ISolicitudes) => {
    const v = {
      ...values,
      campos: values?.campos?.map((ca) => ({
        ...ca,
        opciones:
          ca.opciones && Object.keys(ca.opciones[0])?.length > 0
            ? ca.fuenteDatos == 1
              ? ca.opciones
              : [{ opcion: ca.opciones }]
            : null,
      })),
    };
    if (requestId !== 0) {
      updateRequest({
        ...v,
        id: requestId,
      });
    } else {
      createRequest(v);
    }
  };

  const fuentesDatosOptions = React.useMemo(() => {
    return (fuentesDatos?.data ?? []).map((fd: any) => ({
      label: translatedData.fuentesDatos[fd.fuenteDatoNombre] || fd.fuenteDatoNombre,
      value: fd.fuenteDatoId,
    }));
  }, [fuentesDatos?.data, translatedData.fuentesDatos]);

  const tiposCamposOptions = React.useMemo(() => {
    return (tiposCampos?.data ?? []).map((tc: any) => ({
      label: translatedData.tiposCampos[tc.tipoCampoNombre] || tc.tipoCampoNombre,
      value: tc.tipoCampoId,
    }));
  }, [tiposCampos?.data, translatedData.tiposCampos]);

  const serviciosOptions = React.useMemo(() => {
    return (servicios?.data ?? []).map((sv: any) => ({
      label: translatedData.servicios[sv.servicioNombre] || sv.servicioNombre,
      value: sv.servicioId,
    }));
  }, [servicios?.data, translatedData.servicios]);

  const solicitudesTiposOptions = React.useMemo(() => {
    return (solicitudesTipos?.data ?? []).map((ts: any) => ({
      label: translatedData.tiposSolicitud[ts.tipoNombre] || ts.tipoNombre,
      value: ts.solicitudTipoId,
    }));
  }, [solicitudesTipos?.data, translatedData.tiposSolicitud]);

  const columns = React.useMemo<ColumnsType<ISolicitudes>>(
    () => [
      {
        title: settingsPage?.application_setting_table?.[0]?.label || "Nombre",
        dataIndex: "solicitudNombre",
        render: (text: string) => translatedData.nombres[text] || text,
      },
      {
        title: settingsPage?.application_setting_table?.[1]?.label || "Fecha de creación",
        dataIndex: "fechaCreacion",
      },
      {
        title: settingsPage?.application_setting_table?.[2]?.label || "Estado",
        dataIndex: "estadoNombre",
        render: (text: string) => translatedData.estados[text] || text,
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.solicitudId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditRequest(record),
                },
                {
                  key: "2",
                  label: settingsPage?.delete_button || "Eliminar",
                  icon: <Icon name={record.estadoId === 2 ? "" : ""} color="#A7A7A7" />,
                  onClick: () => changeRequestState(record.solicitudId),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditRequest, changeRequestState, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <SectionHeaderStyled>
              <Skeleton.Input active size="large" style={{ width: 200, marginBottom: 16 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </SectionHeaderStyled>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </>
        ) : (
          <>
            <SectionHeaderStyled>
              <LargeHeadingStyled>{settingsPage?.application__nb || "Solicitudes"}</LargeHeadingStyled>
              <Flex style={{ gap: "15px" }}>
                <Button
                  title=""
                  type="primary"
                  loading={isLoadingById}
                  onClick={() => {
                    setDrawerOpen(true);
                    form.resetFields();
                  }}
                >
                  {settingsPage?.application_detail?.new_application_button || "Nuevo formato de solicitud"}
                </Button>
              </Flex>
            </SectionHeaderStyled>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<ISolicitudes>
                loading={isLoading || isLoadingCreate || isLoadingUpdate || isLoadingState}
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => v.solicitudId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={
                  requestId !== 0 ? (settingsPage?.application_detail?.title || "Edición de formato de solicitud") : (settingsPage?.application_detail?.new_application_button || "Nuevo formato de solicitud")
                }
                open={drawerOpen}
                onClose={() => {
                  setRequestId(0);
                  setDrawerOpen(false);
                }}
              >
            {" "}
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              onFinish={submit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              initialValues={{
                id: 1,
              }}
            >
              <Form.Item
                name="solicitudNombre"
                label={settingsPage?.application_detail?.name || "Nombre de la solicitud"}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Este campo es requerido",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="descripcion" hasFeedback label={settingsPage?.application_detail?.description || "Descripción"}>
                <Input />
              </Form.Item>

              <Form.Item
                name="solicitudTipoId"
                label={settingsPage?.application_detail?.type || "Tipo de solicitud"}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Este campo es requerido",
                  },
                ]}
              >
                <Select
                  size="large"
                  onChange={(e) => setSolicitudTipoId(e)}
                  value={solicitudTipoId}
                  options={solicitudesTiposOptions ?? []}
                  placeholder={settingsPage?.application_detail?.select_option || "Tipo de solicitud"}
                  allowClear
                />
              </Form.Item>
              <Form.Item name="requierePago" valuePropName="checked" hasFeedback>
                <Checkbox
                  onChange={(e) => {
                    setRequierePagoValue(e.target.checked);
                  }}
                >
                  {settingsPage?.application_detail?.required_message || "Requiere pago"}
                </Checkbox>
              </Form.Item>
              {solicitudTipoId === 1 && requierePagoValue ? (
                <Form.Item
                  name="servicioId"
                  label={settingsPage?.application_detail?.service || "Servicio"}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Este campo es requerido",
                    },
                  ]}
                >
                  <Select
                    size="large"
                    options={serviciosOptions ?? []}
                    placeholder={settingsPage?.application_detail?.service || "Servicio"}
                    allowClear
                  />
                </Form.Item>
              ) : null}

              <Form.Item name="urlWebHook" label="URL web hook">
                <Input />
              </Form.Item>
              <Form.Item name="authWebhook" label="Auth web hook">
                <Input />
              </Form.Item>

              {/* Nest Form.List */}
              <Form.Item label={settingsPage?.application_detail?.field || "Campos"}>
                <Form.List name={["campos"]}>
                  {(subFields, subList) => (
                    <div>
                      {subFields.map((subField, index) => (
                        <div key={subField.key} style={{ position: "relative", marginTop: 16 }}>
                          <Button
                            type="text"
                            style={{ position: "absolute", top: -15, right: 0 }}
                            onClick={() => {
                              subList.remove(subField.name);
                            }}
                          >
                            <Icon name="" color="#bcbbbb" />
                          </Button>

                          <Form.Item
                            name={[subField.name, "campoNombre"]}
                            label={settingsPage?.application_detail?.field_name || "Nombre del campo"}
                            required
                          >
                            <Input placeholder={settingsPage?.application_detail?.field_name || "Nombre del campo"} />
                          </Form.Item>
                          <Form.Item
                            name={[subField.name, "tipoCampoId"]}
                            label={settingsPage?.application_detail?.field_type || "Tipo de campo"}
                            hasFeedback
                            required
                          >
                            <Select
                              allowClear
                              size="large"
                              options={tiposCamposOptions ?? []}
                              placeholder={settingsPage?.application_detail?.field_type || "Tipo de campo"}
                            />
                          </Form.Item>
                          {formularioCampos && formularioCampos[index]?.tipoCampoId == 4 && (
                            <>
                              <Form.Item
                                name={[subField.name, "fuenteDatos"]}
                                  label={settingsPage?.application_detail?.database || "Fuente de datos"}
                                hasFeedback
                                required
                              >
                                <Select
                                  allowClear
                                  size="large"
                                  options={fuentesDatosOptions ?? []}
                                  placeholder={settingsPage?.application_detail?.field_type || "Tipo de campo"}
                                />
                              </Form.Item>
                              {formularioCampos && formularioCampos[index]?.fuenteDatos == 1 ? (
                                <Form.Item
                                    label={settingsPage?.application_detail?.options || "Opciones"}
                                  required
                                  style={{
                                    backgroundColor: "#f1f1f6",
                                    borderRadius: 10,
                                    padding: 25,
                                  }}
                                >
                                  <Form.List name={[subField.name, "opciones"]}>
                                    {(subOptions, subOpt) => (
                                      <div>
                                        {subOptions.map((subOption) => (
                                          <div
                                            key={subOption.key}
                                            style={{ position: "relative", marginTop: 16 }}
                                          >
                                            <Button
                                              type="text"
                                              style={{ position: "absolute", top: -15, right: 0 }}
                                              onClick={() => {
                                                subOpt.remove(subOption.name);
                                              }}
                                            >
                                              <Icon name="" color="#bcbbbb" />
                                            </Button>

                                            <Form.Item
                                              name={[subOption.name, "opcion"]}
                                                      label={settingsPage?.application_detail?.option_name || "Nombre de la opción"}
                                              required
                                            >
                                              <Input placeholder={settingsPage?.application_detail?.option || "Opción"} />
                                            </Form.Item>
                                          </div>
                                        ))}
                                        <Button type="dashed" onClick={() => subOpt.add()} block>
                                          + {settingsPage?.application_detail?.add_field || "Añadir opción"}
                                        </Button>
                                      </div>
                                    )}
                                  </Form.List>
                                </Form.Item>
                              ) : formularioCampos &&
                                (formularioCampos[index]?.fuenteDatos == 2 ||
                                  formularioCampos[index]?.fuenteDatos == 3) ? (
                                <Form.Item
                                  name={[subField.name, "opciones"]}
                                  label={
                                    formularioCampos[index]?.fuenteDatos == 2
                                      ? (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.procedure_name || "Nombre del procedimiento")
                                      : (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.webservice_name || "Nombre del web service")
                                  }
                                  required
                                >
                                  <Input placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.name_ap || "Nombre"} />
                                </Form.Item>
                              ) : (
                                <></>
                              )}
                            </>
                          )}

                          <Form.Item
                            name={[subField.name, "esObligatorio"]}
                            valuePropName="checked"
                            hasFeedback
                          >
                            <Checkbox>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.isrequired || "Es obligatorio"}</Checkbox>
                          </Form.Item>
                          <Form.Item
                            name={[subField.name, "esAdministrativo"]}
                            valuePropName="checked"
                            hasFeedback
                          >
                            <Checkbox>Es administrativo</Checkbox>
                          </Form.Item>
                          <Form.Item
                            name={[subField.name, "informacionAdicional"]}
                            label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.additional_info || "Información adicional"}
                          >
                            <Input placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.additional_info || "Información adicional"} />
                          </Form.Item>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => subList.add()} block>
                        + {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : settingsPage?.application_detail?.add_field || "Añadir campo"}
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Form.Item>

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingCreate || isLoadingState || isLoadingUpdate}
              >
                {settingsPage?.application_detail?.save_button || "Guardar"}
              </Button>
            </Form>
          </Drawer>
            </Card>
          </>
        )}
      </Content>
    </Flex>
  );
};

export default RequestsConfig;
